"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  product_cat: 17,
  per_page: 3
};
const wp_query = new WP_Query(wp_query_params);
function BlendsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-md border border-border bg-card p-5 shadow-xs">
            <div className="h-56 w-full animate-pulse rounded bg-muted/60" />
            <div className="mt-4 h-4 w-24 animate-pulse rounded bg-muted/60" />
            <div className="mt-2 h-6 w-3/4 animate-pulse rounded bg-muted/60" />
            <div className="mt-4 flex items-center justify-between pt-3">
              <div className="h-6 w-20 animate-pulse rounded bg-muted/60" />
              <div className="h-9 w-28 animate-pulse rounded bg-muted/60" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-md border border-border bg-muted/40 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to retrieve peptide blend formulations.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          Retry request
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="rounded-md border border-dashed border-border p-12 text-center">
        <FlaskConical className="mx-auto size-8 text-muted-foreground/60" />
        <p className="mt-3 text-sm font-medium text-foreground">
          No blend formulations available
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Check back shortly or view our single-reagent peptide catalogue.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const ratioText = product.customFields?.molar_ratio || "1:1 MOLAR RATIO";
      const lotNumber = product.customFields?.batch_no || "LOT-VERIFIED";
      return <Card key={product.id} data-index={i} className="group flex flex-col justify-between rounded-md border border-border bg-card p-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-sm">
            <div>
              <div className="relative flex h-56 w-full items-center justify-center overflow-hidden rounded-sm bg-muted/50 p-4">
                <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                  <Badge variant="outline" className="border-primary/30 bg-background font-mono text-[11px] font-medium tracking-wide text-primary">
                    {ratioText}
                  </Badge>
                </div>
                <div className="absolute top-2.5 right-2.5">
                  <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] font-semibold text-secondary-foreground">
                    <CheckCircle2 className="size-3 text-chart-2" />
                    {lotNumber}
                  </span>
                </div>

                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-contain transition-opacity duration-200 group-hover:opacity-95" /> : <div className="flex flex-col items-center justify-center text-muted-foreground/50">
                    <FlaskConical className="size-12 stroke-[1.5]" />
                    <span className="mt-2 font-mono text-xs text-muted-foreground">
                      DUAL VIAL UNIT
                    </span>
                  </div>}
              </div>

              <div className="mt-4">
                <p className="font-mono text-xs text-muted-foreground">
                  LYOPHILIZED COMPOSITE
                </p>
                <h3 className="mt-1 text-base font-semibold text-foreground">
                  <Link to={product.link} className="transition-colors hover:text-primary">
                    {product.title}
                  </Link>
                </h3>

                {product.short_description ? <div className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground [&_p]:m-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : <p className="mt-2 text-xs text-muted-foreground">
                    High-performance dual reagent blend for cellular receptor validation assays.
                  </p>}
              </div>
            </div>

            <CardContent className="mt-5 border-t border-border p-0 pt-4">
              <div className="flex items-baseline justify-between gap-2 pb-3">
                <span className="text-xs font-medium text-muted-foreground">
                  Analytical Grade
                </span>
                <ProductPrice product={product} className="font-mono text-base font-semibold text-foreground" />
              </div>

              <div className="mt-1">
                <AddToCartButton product={product} productId={product.id} variant="default" size="default" text="Add to cart" buttonClassName="bg-primary text-foreground-primary w-full text-xs font-semibold uppercase tracking-wider" showQuantity={false} className="w-full" />
              </div>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function Blends() {
  return <section id="featured-peptide-blends" data-nav="light" className="bg-background py-14 md:py-20">
      <div className="mx-auto max-w-[1088px] px-6 md:px-8">
        <div className="mb-8 flex flex-col items-start justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-end">
          <div>
            <span className="font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Combinatorial Reagents
            </span>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              Featured peptide blends
            </h2>
          </div>

          <Button asChild variant="outline" size="sm" className="shrink-0">
            <Link to="/ShopPage" className="inline-flex items-center gap-1.5 font-medium">
              <span>See All Blends</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>

        <EcommerceProductsProvider wp_query={{
        product_cat: [17],
        per_page: 3
      }}>
          <BlendsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}